// Listing Builder (taki magazasi): FOTOGRAFI URETILMIS urunler. Taslagi
// olmayanlar one cikar; tek tikla liste olusturulur ya da secilenler toplu
// Liste Denetimi'ne gonderilir.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, ChevronDown, ChevronRight, ClipboardCheck, Loader2, Plus, Search } from "lucide-react";
import { getBackendUrl } from "../../lib/http";
import { useJewelryTypes } from "../../lib/useJewelryTypes";
import { useShotProducts, type ShotProduct } from "../../lib/useShotProducts";

const abs = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

const DRAFT_STATUS_TR: Record<string, string> = {
  draft: "Taslak", ready: "Hazır", scheduled: "Planlı", published: "Yayında", archived: "Arşiv",
};

type DraftFilter = "all" | "none" | "has";

export function ShotProductsPanel({ standalone = false }: { standalone?: boolean } = {}) {
  const navigate = useNavigate();
  const types = useJewelryTypes();
  const { data, isLoading } = useShotProducts();
  const items = data?.items ?? [];
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [catalog, setCatalog] = useState("");
  const [draftFilter, setDraftFilter] = useState<DraftFilter>("none");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const typeLabel = (k: string | null) => (k ? types.find((t) => t.key === k)?.label ?? k : "Diğer");
  const presentTypes = useMemo(() => [...new Set(items.map((i) => i.typeKey ?? ""))], [items]);
  const presentCatalogs = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items) m.set(i.catalogId, i.catalogName || "Katalog");
    return [...m.entries()];
  }, [items]);

  const filtered = useMemo(() => items.filter((i) => {
    if (type && (i.typeKey ?? "") !== type) return false;
    if (catalog && i.catalogId !== catalog) return false;
    if (draftFilter === "none" && i.drafts.length > 0) return false;
    if (draftFilter === "has" && i.drafts.length === 0) return false;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const sku = i.product?.source?.sku?.toLowerCase() ?? "";
      if (!i.name.toLowerCase().includes(s) && !sku.includes(s)) return false;
    }
    return true;
  }), [items, type, catalog, draftFilter, q]);

  const withoutDraft = items.filter((i) => i.drafts.length === 0).length;
  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const sendToReview = (keys: string[]) => navigate(`/listing-review/jewelry?products=${encodeURIComponent(keys.join(","))}`);
  const builderUrl = (i: ShotProduct) => `/listing-builder/jewelry?catalogId=${i.catalogId}&categoryId=${i.categoryId}`;

  if (!isLoading && items.length === 0) {
    return standalone ? (
      <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-sm text-gray-500">
        Henüz fotoğrafı üretilmiş ürün yok. Katalogdan bir ürün seçip Takı Studio'da konsept fotoğrafı üretin.
        <div className="mt-3 flex justify-center gap-2">
          <Link to="/supplier-catalogs" className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-50">Katalog</Link>
          <Link to="/jewelry-studio" className="rounded-lg bg-fuchsia-600 px-3 py-1.5 text-white hover:bg-fuchsia-700">Studio</Link>
        </div>
      </div>
    ) : null;
  }

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white">
      <button onClick={() => !standalone && setOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {!standalone && (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
          <Camera className="h-4 w-4 text-fuchsia-500" /> Fotoğrafı üretilmiş ürünler
          <span className="text-xs font-normal text-gray-500">({items.length} ürün · {withoutDraft} tanesinin taslağı yok)</span>
        </span>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </button>
      {(open || standalone) && (
        <div className="border-t border-gray-100 p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
              {([["none", "Taslağı yok"], ["has", "Taslağı var"], ["all", "Tümü"]] as Array<[DraftFilter, string]>).map(([k, l]) => (
                <button key={k} onClick={() => setDraftFilter(k)}
                  className={`rounded-md px-2.5 py-1 font-medium ${draftFilter === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                  {l}
                </button>
              ))}
            </div>
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5">
              <option value="">Tüm kategoriler</option>
              {presentTypes.map((k) => <option key={k || "none"} value={k}>{typeLabel(k || null)} ({items.filter((i) => (i.typeKey ?? "") === k).length})</option>)}
            </select>
            {presentCatalogs.length > 1 && (
              <select value={catalog} onChange={(e) => setCatalog(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5">
                <option value="">Tüm kataloglar</option>
                {presentCatalogs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı veya SKU"
                className="w-52 rounded-lg border border-gray-200 py-1.5 pl-8 pr-2" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {filtered.length > 0 && (
                <button onClick={() => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((i) => i.key)))}
                  className="text-gray-500 underline hover:text-gray-700">
                  {selected.size === filtered.length ? "Seçimi kaldır" : "Hepsini seç"}
                </button>
              )}
              <button onClick={() => sendToReview(selected.size ? [...selected] : filtered.map((i) => i.key))}
                disabled={filtered.length === 0}
                className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                <ClipboardCheck className="h-3.5 w-3.5" />
                {selected.size ? `Seçilenleri denetle (${selected.size})` : `Görünenleri denetle (${filtered.length})`}
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-gray-400">Bu filtrede ürün yok.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {filtered.map((i) => (
                <div key={i.key} className={`overflow-hidden rounded-lg border ${selected.has(i.key) ? "border-fuchsia-500 ring-1 ring-fuchsia-300" : "border-gray-200"}`}>
                  <button onClick={() => toggle(i.key)} className="relative block aspect-square w-full bg-gray-50" title="Seç">
                    <img src={abs(i.coverUrl)} alt="" className="h-full w-full object-cover" loading="lazy" />
                    <input type="checkbox" readOnly checked={selected.has(i.key)} className="absolute left-1.5 top-1.5 h-4 w-4" />
                    <span className="absolute right-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">{i.shotCount} foto</span>
                  </button>
                  <div className="space-y-1 p-2 text-[11px]">
                    <p className="line-clamp-2 font-medium text-gray-800" title={i.name}>{i.name}</p>
                    <p className="text-gray-400">{typeLabel(i.typeKey)}{i.product?.source?.sku ? ` · ${i.product.source.sku}` : ""}</p>
                    {i.drafts.length > 0 ? (
                      <Link to={`/listing-builder?id=${i.drafts[0].id}`} className="block truncate text-emerald-700 hover:underline" title={i.drafts[0].title}>
                        ✓ {DRAFT_STATUS_TR[i.drafts[0].status] ?? i.drafts[0].status}{i.drafts.length > 1 ? ` (+${i.drafts.length - 1})` : ""}
                      </Link>
                    ) : (
                      <p className="text-amber-600">Taslak yok</p>
                    )}
                    <div className="flex gap-1 pt-0.5">
                      <Link to={builderUrl(i)} className="flex flex-1 items-center justify-center gap-1 rounded border border-gray-200 py-1 text-gray-700 hover:bg-gray-50">
                        <Plus className="h-3 w-3" /> Liste
                      </Link>
                      <button onClick={() => sendToReview([i.key])} className="flex flex-1 items-center justify-center gap-1 rounded border border-fuchsia-200 py-1 text-fuchsia-700 hover:bg-fuchsia-50">
                        <ClipboardCheck className="h-3 w-3" /> Denetle
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
